import Image from 'next/legacy/image';
import {
  Header,
  Footer,
  HeroBigWebGL,
  Section,
  SceneViewer,
  SectionTitle,
  Video
} from 'react-scale/components';
import Slider from 'react-scale/components/pages/customers/Slider';

import InteractiveCar from 'react-scale/components/website/big/open-datasets/shared/InteractiveCar';
import SensorCalibration from 'react-scale/components/website/big/open-datasets/shared/SensorCalibrationComponent';
import DataAnnotation from 'react-scale/components/website/big/open-datasets/shared/DataAnnotationComponent';
import InstancesPerLabel from 'react-scale/components/website/big/open-datasets/shared/InstancesPerLabel';

import scenes from 'react-scale/components/website/big/open-datasets/nuscenes/LidarScenesFrom1To10';

const Nuscenes = () => (
  <div>
    <div className="relative">
      <Header
        title="nuScenes Open Datasets - Scale"
        canonical="https://scale.com/open-av-datasets/nuscenes"
        favicon="open-datasets/nuscenes"
        ogImg="https://scale.com/static/img/website/open-datasets/nuscenes/social/fb.png"
        twImg="https://scale.com/static/img/website/open-datasets/nuscenes/social/tw.png"
      />
      <HeroBigWebGL
        title="nuScenes by Motional"
        subtitle="Large-scale open source dataset for autonomous driving. <br/><br/> For commercial use, please visit the <a className='font-bold underline' href='https://nuscenes.org/' target='_blank'>nuScenes website directly</a>."
        webgl={{
          style: {
            '--gradient-color-1': '#5C48F6',
            '--gradient-color-2': '#5C48F6',
            '--gradient-color-3': '#000',
            '--gradient-color-4': '#000'
          }
        }}
        cta={{
          buttons: [
            {
              href: 'https://www.google.com/url?q=https://colab.research.google.com/github/nutonomy/nuscenes-devkit/blob/master/python-sdk/tutorials/nuscenes_tutorial.ipynb&sa=D&source=docs&ust=1663879165749796&usg=AOvVaw1SN9_xuCJQI6AmYqoy8Tec',
              text: 'Download Dataset',
              type: 'primary',
              borderColor: '#5C48F6'
            },
            { href: '/about', text: 'Learn More About Scale', type: 'secondary' }
          ]
        }}
      />
    </div>
    <Section bg="bg-neutral-950">
      <SceneViewer
        scenes={scenes}
        viewerTheme={{
          hover: '#5C48F6',
          color: '#000'
        }}
      />
    </Section>
    <Section paddingBottom={false}>
      <SectionTitle
        overtitle="Overview"
        title="Support for computer vision and autonomous driving research"
        subtitle="nuScenes by Motional is a public, large-scale dataset for autonomous driving. It enables researchers to study challenging urban driving scenarios using the full suite of sensors from a real autonomous vehicle. Motional made nuScenes and the follow-on datasets publicly available to help advance industry research."
      />
    </Section>
    <Slider slides={slides} />
    <Section>
      <SectionTitle
        overtitle="Data Collection"
        title="Careful scene planning by Motional"
        subtitle="The nuScenes dataset features approximately 15h of real-world driving data from Boston and Singapore, including Boston's new Seaport district and Singapore’s One North, Queenstown, and Holland Village districts. Driving routes are carefully chosen to capture challenging scenarios, including locations, times, and weather conditions."
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {mapAssets.map(asset => (
          <div className="overflow-hidden rounded-lg aspect-video">
            <iframe src={asset.src} width="100%" height="100%" />
          </div>
        ))}
      </div>
    </Section>
    <Section bg="bg-neutral-950 overflow-hidden">
      <SectionTitle
        overtitle="Car Setup"
        title="Vehicle, Sensor and Camera Details"
        subtitle="Two identical cars with identical sensor layouts were used to drive in Boston and Singapore. <br /> Refer to the image below for the placement of the sensors:"
      />
      <InteractiveCar
        dataset="nuscenes"
        background="#5c48f6"
        items={[
          {
            type: 'camera',
            text: 'Cameras',
            number: '6',
            background: '#5C48F6',
            content: [
              '12Hz capture frequency',
              'Evetar Lens N118B05518W F1.8 f5.5mm 1/1.8"',
              '1/1.8" CMOS sensor of 1600x1200 resolution',
              'Bayer8 format for 1 byte per pixel encoding',
              '1600x900 ROI is cropped from the original resolution to reduce processing and transmission bandwidth',
              'Auto exposure with exposure time limited to the maximum of 20 ms',
              'Images are unpacked to BGR format and compressed to JPEG'
            ]
          },
          {
            type: 'lidar',
            text: 'Spinning LiDAR',
            number: '1',
            background: '#5C48F6',
            content: [
              '20Hz capture frequency',
              '32 beams, 1080 (+-10) points per ring',
              '32 channels',
              '360° Horizontal FOV, +10° to -30° Vertical FOV, uniform azimuth angles',
              '80m-100m Range, Usable returns up to 70 meters, ± 2 cm accuracy',
              'Up to ~1.39 Million Points per Second'
            ]
          },
          {
            type: 'radar',
            text: 'Long Range RADAR Sensor',
            number: '5',
            background: '#5C48F6',
            content: [
              '77GHz',
              '13Hz capture frequency',
              'Independently measures distance and velocity in one cycle using Frequency Modulated Continuous Wave',
              'Up to 250m distance',
              'Velocity accuracy of ±0.1 km/h'
            ]
          }
        ]}
      />
    </Section>
    <Section>
      <SectionTitle title="Flythrough of the nuScenes Teaser" />
      <Video
        url="https://www.youtube.com/watch?v=eHvGiddaNiM"
        poster="/static/img/website/open-datasets/nuscenes/nuscenes-video-poster.jpg"
      />
    </Section>
    <Section bg="bg-neutral-950">
      <SectionTitle
        overtitle="Sensor Calibration"
        title="Our multi-sensor dataset uses sensors that have been calibrated for the extrinsics and intrinsics of every sensor"
        fullWidth
      />
      <SensorCalibration dataset="nuscenes" />
    </Section>
    <Section>
      <SectionTitle
        title="Sensor Synchronization"
        subtitle="In order to achieve cross-modality data alignment between the LiDAR and the cameras, the exposure on each camera was triggered when the top LiDAR sweeps across the center of the camera’s FOV. This method was selected as it generally yields good data alignment. Note that the cameras run at 12Hz while the LiDAR runs at 20Hz.<br><br>The 12 camera exposures are spread as evenly as possible across the 20 LiDAR scans, so not all LiDAR scans have a corresponding camera frame.<br><br>Reducing the frame rate of the cameras to 12Hz helps to reduce the compute, bandwidth and storage requirement of the perception system."
      />
      <div className="relative">
        <Image
          src="https://cdn.builder.io/api/v1/image/assets%2Fe0438815ba51486bbb6a202747122d4b%2F64b4117c43ee49f3b152b0b2ab90634e"
          alt="Sensor Synchronization Capture"
          width={2720}
          height={1248}
          quality={100}
          unoptimized
        />
      </div>
    </Section>
    <Section bg="bg-neutral-950">
      <SectionTitle overtitle="Data Annotation" title="Complex Label Taxonomy" />
      <DataAnnotation activeItemColor="#5C48F6" scenes={scenes} />
    </Section>
    <InstancesPerLabel
      items={instancesPerLabelItems}
      background="#5C48F6"
      link={{
        href: 'https://www.nuscenes.org/data-annotation',
        text: 'More details'
      }}
      sectionTopPadding
      smallNumbers
    />
    <Section>
      <SectionTitle
        title="Get Started with nuScenes"
        subtitle="Ready to get started with nuScenes? This tutorial will give you an overview of the dataset without the need to download it."
        link={{
          href: 'https://www.google.com/url?q=https://colab.research.google.com/github/nutonomy/nuscenes-devkit/blob/master/python-sdk/tutorials/nuscenes_tutorial.ipynb&sa=D&source=docs&ust=1663879165749796&usg=AOvVaw1SN9_xuCJQI6AmYqoy8Tec',
          text: 'Take the Tutorial',
          openInNewTab: true
        }}
      />
      <div className="relative">
        <Image
          src="https://cdn.builder.io/api/v1/image/assets%2Fe0438815ba51486bbb6a202747122d4b%2Ffad862eb9b8e418d80bebbf7602ba50b"
          alt="Sensor Synchronization Capture"
          width={2000}
          height={562}
          quality={100}
          unoptimized
        />
      </div>
    </Section>
    <Footer theme="dark" border />
  </div>
);

const slides = [
  {
    url: 'https://cdn.builder.io/api/v1/image/assets%2Fe0438815ba51486bbb6a202747122d4b%2F4fbedd4b566c4523977681f92d2440cc',
    alt: 'car1'
  },
  {
    url: 'https://cdn.builder.io/api/v1/image/assets%2Fe0438815ba51486bbb6a202747122d4b%2F7c63854c5c2d4ac79e4f67b840c38e44',
    alt: 'car2'
  }
];

const mapAssets = [
  {
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11795.238448457265!2d-71.04575156162444!3d42.3465823260751!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e37a831865f8c5%3A0xad7eb0e04b6892d9!2sSeaport%20District%2C%20Boston%2C%20MA%2C%20USA!5e0!3m2!1sen!2sar!4v1575393360363!5m2!1sen!2sar',
    name: 'Boston Seaport'
  },
  {
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63820.99154460931!2d103.74875838842179!3d1.2868990097043849!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da1a5234b05815%3A0x500f7acaedaa4b0!2sQueenstown%2C%20Singapore!5e0!3m2!1sen!2sar!4v1575393442173!5m2!1sen!2sar',
    name: 'Singapore Queenstown'
  },
  {
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9065.670271856141!2d103.78421610939874!3d1.303005174254609!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xc5a49606699d4811!2sone-north!5e0!3m2!1sen!2sar!4v1575394007318!5m2!1sen!2sar',
    name: 'Singapore One North'
  },
  {
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.770820819013!2d103.79626291631077!3d1.312947889856664!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da1a7043303b6d%3A0xc7a439d5d1637e5!2sHolland%20Rd%2C%20Singapore!5e0!3m2!1sen!2sar!4v1575394404176!5m2!1sen!2sar',
    name: 'Singapore Holland Village'
  }
];

const instancesPerLabelItems = [
  {
    instances: '493,322',
    label: 'vehicle.car'
  },
  {
    instances: '208,240',
    label: 'human.pedestrian.adult'
  },
  {
    instances: '152,087',
    label: 'movable_object.barrier'
  },
  {
    instances: '208,240',
    label: 'movable_object.trafficcone'
  },
  {
    instances: '88,519',
    label: 'vehicle.truck'
  },
  {
    instances: '24,860',
    label: 'vehicle.trailer'
  },
  {
    instances: '14,671',
    label: 'vehicle.construction'
  },
  {
    instances: '14,501',
    label: 'vehicle.bus.rigid'
  }
];

Nuscenes.theme = 'dark';

export default Nuscenes;
